<template>
  <WindowedCallStage
    :local-stream="localStream"
    :remote-participants="participants"
    :local-ready="publishing"
    :local-title="localTitle"
    :pre-answer-title="preAnswerTitle"
    :muted-local="!micOn"
    :video-on-local="camOn"
    :session-role="'caller'"
    :call-status="callStatus"
    :show-local-window="true"
    :layer-visibility="vcStore?.settings?.layerVisibility || defaultLayers"
    :segmentation-enabled="!!vcStore?.settings?.useSegmentation"
    :avatar-has-face-rig="!!selectedAvatarId"
    :is-fullscreen="false"
    :has-avatar="!!selectedAvatarId"
    :avatar-options="avatarOptions"
    :selected-avatar-id="selectedAvatarId"
    :avatars-loading="avatarsLoading"
    :expression-preset-options="expressionPresetOptions"
    :selected-expression-preset="selectedExpressionPreset"
    :eye="vcStore?.settings?.eye || {}"
    :iris-style="vcStore?.settings?.irisStyle || 'vector'"
    :selected-background-id="vcStore?.settings?.backgroundId || ''"
    :voice-settings="vcStore?.settings?.voice || {}"
    :remote-tracks-changed-at="remoteTracksChangedAt"
    @mute-toggle="onMuteToggle"
    @toggle-video="onToggleVideo"
    @hangup="onHangupFull"
    @action="onActionMenu"
    @open:refine-selection="onOpenRefineFromWindow"
    @background-selected="onBgSelect"
    @update:selectedAvatarId="onUpdateSelectedAvatar"
    @update:selectedExpressionPreset="onUpdateExpressionPreset"
    @update:eye="onUpdateEye"
    @update:irisStyle="onUpdateIris"
    @update:voiceSettings="onUpdateVoice"
  />
  <RefineSelectionDialog
    v-model="showRefineDialog"
    :image-src="selectedAvatar?.src || ''"
    :initial-mask-src="initialRefineMask || precomputedHairMaskForCurrentAvatar"
    @save="onRefineSaved"
    @delete="onRefineDeleted"
  />
</template>

<script setup>
  import { computed, ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
  import WindowedCallStage from '../../components/WindowedCallStage.vue'
  import RefineSelectionDialog from '../../components/common/RefineSelection/RefineSelectionDialog.vue'
  import { useSfuRoom } from '../composables/useSfuRoom'
  import { useCharacterStore } from '@/stores/characterStore'
  import { useCampaignStore } from '@/stores/campaignStore'
  import { useUserStore } from '@/stores/userStore'
  import { useSfuCallControlStore } from '../stores/sfuCallControlStore'
  import avatarEngine from '../../services/avatarEngine'
  import mediaUploadService from '../../services/mediaUploadService'
  import { CHAT_BACKGROUND_PUBLIC_PATH } from '../../config/backgrounds'
  import { createDebugLogger } from '../../config/debugLog'

  const sfuDebug = createDebugLogger('sfu')

  const EXPRESSION_PRESETS = Object.freeze([
    {
      id: 'subtle',
      label: 'Subtle',
      values: {
        expressionStrength: 0.86,
        faceFitStrength: 0.6,
        mouthBoost: 1.28,
        mouthSmoothing: 0.78,
        browStrength: 0.78,
        browSmoothing: 0.72,
        cheekStrength: 0.74,
        cheekSmoothing: 0.7
      }
    },
    {
      id: 'natural',
      label: 'Natural',
      values: {
        expressionStrength: 1.0,
        faceFitStrength: 0.62,
        mouthBoost: 1.45,
        mouthSmoothing: 0.68,
        browStrength: 1.0,
        browSmoothing: 0.58,
        cheekStrength: 1.0,
        cheekSmoothing: 0.56
      }
    },
    {
      id: 'expressive',
      label: 'Expressive',
      values: {
        expressionStrength: 2.46,
        faceFitStrength: 0.72,
        mouthBoost: 3.15,
        mouthSmoothing: 0.42,
        browStrength: 1.56,
        browSmoothing: 0.34,
        cheekStrength: 3.58,
        cheekSmoothing: 0.32
      }
    }
  ])

  const DEFAULT_EXPRESSION_PRESET_ID = 'expressive'

  function resolveExpressionPreset(id) {
    const key = String(id || '')
      .trim()
      .toLowerCase()
    return (
      EXPRESSION_PRESETS.find(
        (row) =>
          String(row.id || '')
            .trim()
            .toLowerCase() === key
      ) ||
      EXPRESSION_PRESETS.find((row) => row.id === DEFAULT_EXPRESSION_PRESET_ID) ||
      EXPRESSION_PRESETS[0]
    )
  }

  const props = defineProps({
    conversationId: { type: String, required: true },
    peerId: { type: String, required: true }
  })

  const characterStore = useCharacterStore()
  const campaignStore = useCampaignStore()
  const userStore = useUserStore()
  const ctrl = useSfuCallControlStore()
  const {
    localStream,
    remoteStreams,
    remotePeers,
    publishing,
    join,
    startLocal,
    leave,
    setPeerVideoLayer,
    pausePeerVideo,
    resumePeerVideo,
    updateSettings,
    vcStore
  } = useSfuRoom({
    conversationId: props.conversationId,
    peerId: props.peerId
  })

  const defaultLayers = {
    background: true,
    camera: true,
    avatarFace: true,
    avatarEyes: true,
    avatarHair: false
  }
  const micOn = ref(true)
  const camOn = ref(true)
  const callStatus = ref('ringing') // 'ringing' | 'connecting' | 'active' | 'ended'
  const avatarsLoading = ref(false)
  const avatarOptions = ref([])
  const remoteTracksChangedAt = ref(0) // Updated whenever remoteStreams changes
  const showRefineDialog = ref(false)
  const initialRefineMask = ref('')
  const hadOutgoingTargets = ref(false)
  const hadAnyRemoteConnection = ref(false)
  const seenConnectedPeerIds = new Set()
  let noAnswerAutoCloseTimer = null
  let callEndedAutoCloseTimer = null

  const selectedAvatarId = computed(() => vcStore?.settings?.avatarId || '')
  const settingsCharacterId = computed(() => {
    const avatarId = String(selectedAvatarId.value || '').trim()
    if (avatarId.startsWith('character:')) {
      const id = avatarId.replace(/^character:/, '').trim()
      if (id) return id
    }
    const current = String(characterStore.currentCharacter?.id || '').trim()
    if (current) return current
    const first = String(characterStore.characters?.[0]?.id || '').trim()
    return first || ''
  })
  const selectedAvatar = computed(() => {
    const id = String(selectedAvatarId.value || '').trim()
    if (!id) return null
    return avatarOptions.value.find((o) => String(o?.id || '').trim() === id) || null
  })
  const expressionPresetOptions = computed(() =>
    EXPRESSION_PRESETS.map((row) => ({ label: row.label, value: row.id }))
  )
  const selectedExpressionPreset = computed(() => {
    const raw = String(vcStore?.settings?.expressionPresetId || '').trim()
    return resolveExpressionPreset(raw || DEFAULT_EXPRESSION_PRESET_ID).id
  })
  const isCampaignConversation = computed(() =>
    String(props.conversationId || '')
      .trim()
      .startsWith('campaign:')
  )
  const campaignIdFromConversation = computed(() =>
    String(props.conversationId || '')
      .replace(/^campaign:/, '')
      .trim()
  )
  const campaignName = ref('')
  let campaignLookupSeq = 0

  async function resolveCampaignName(id) {
    const campaignKey = String(id || '').trim()
    if (!campaignKey) return ''
    try {
      const list = Array.isArray(campaignStore.getCampaigns) ? campaignStore.getCampaigns : []
      const cached = list.find((c) => String(c?.id || '').trim() === campaignKey)
      const current =
        String(campaignStore.getCurrentCampaign?.id || '').trim() === campaignKey
          ? campaignStore.getCurrentCampaign
          : null
      const fromCache = cached || current || null
      const cachedName = String(fromCache?.name || fromCache?.title || '').trim()
      if (cachedName) return cachedName
    } catch {}
    try {
      const fetched = await campaignStore.fetchCampaign?.(campaignKey)
      return String(fetched?.name || fetched?.title || '').trim()
    } catch {
      return ''
    }
  }

  const preAnswerTitle = computed(() => {
    if (!isCampaignConversation.value) return ''
    const campaign = String(campaignName.value || '').trim()
    return campaign ? `Group call ${campaign}` : 'Group call'
  })

  const precomputedHairMaskForCurrentAvatar = computed(() => {
    try {
      const base = vcStore?.settings || {}
      const key = String(selectedAvatarId.value || selectedAvatar.value?.src || '').trim()
      if (key && base.avatarCutouts && typeof base.avatarCutouts === 'object') {
        const row = base.avatarCutouts[key]
        const src = row?.maskSrc || row?.hairMaskSrc
        if (typeof src === 'string' && src.length) return src
      }
      const fallback = base.precomputedAvatarHairMaskSrc
      return typeof fallback === 'string' ? fallback : ''
    } catch {
      return ''
    }
  })
  const localTitle = computed(() => {
    const id = selectedAvatarId.value
    if (id) {
      const opt = avatarOptions.value.find((o) => o.id === id)
      if (opt && opt.label) return opt.label
    }
    return 'You'
  })

  watch(
    () => campaignIdFromConversation.value,
    async (id) => {
      campaignName.value = ''
      if (!isCampaignConversation.value || !id) return
      const seq = ++campaignLookupSeq
      const name = await resolveCampaignName(id)
      if (seq !== campaignLookupSeq) return
      campaignName.value = name
    },
    { immediate: true }
  )

  const userProfiles = ref({}) // uid -> { displayName, photoURL }
  const intendedTargetIds = computed(() => {
    const selfId = String(props.peerId || '').trim()
    return (Array.isArray(ctrl.current?.targets) ? ctrl.current.targets : [])
      .map((id) => String(id || '').trim())
      .filter((id) => id && id !== selfId)
  })
  const hasIntendedTargets = computed(() => intendedTargetIds.value.length > 0)

  function normalizePeerIds(list) {
    return Array.from(
      new Set(
        (Array.isArray(list) ? list : []).map((id) => String(id || '').trim()).filter(Boolean)
      )
    )
  }

  function removeIntendedTargets(peerIds, reason = '') {
    const ids = normalizePeerIds(Array.isArray(peerIds) ? peerIds : [peerIds])
    if (!ids.length) return false
    const currentTargets = Array.isArray(ctrl.current?.targets) ? ctrl.current.targets : []
    if (!currentTargets.length) return false
    const idSet = new Set(ids)
    const nextTargets = currentTargets.filter((id) => !idSet.has(String(id || '').trim()))
    if (nextTargets.length === currentTargets.length) return false
    ctrl.current.targets = nextTargets
    sfuDebug('[SFU] targets pruned', {
      reason: reason || 'unknown',
      removed: ids,
      remaining: nextTargets.length
    })
    return true
  }

  function clearIntendedTargets(reason = '') {
    const currentTargets = Array.isArray(ctrl.current?.targets) ? ctrl.current.targets : []
    if (!currentTargets.length) return false
    ctrl.current.targets = []
    sfuDebug('[SFU] targets cleared', {
      reason: reason || 'unknown',
      removed: currentTargets.length
    })
    return true
  }

  // Merge intended targets (mass call) + peers (in room) with streams (media available)
  const participants = computed(() => {
    const byId = {}
    const intended = intendedTargetIds.value
    const selfId = String(props.peerId || '').trim()
    const peerIds = (remotePeers.value || [])
      .map((id) => String(id || '').trim())
      .filter((id) => id && id !== selfId)
    const baseIds = Array.from(new Set([...(intended || []), ...peerIds]))
    for (const id of baseIds) {
      const p = userProfiles.value[id] || null
      byId[id] = {
        key: id,
        uid: id,
        stream: null,
        name: (p && p.displayName) || id || 'User',
        avatar: (p && p.photoURL) || ''
      }
    }
    for (const r of remoteStreams.value || []) {
      const id = String(r.peerId || r.uid || '').trim()
      if (!id || id === selfId) continue
      byId[id] = {
        ...(byId[id] || {}),
        key: id,
        uid: id,
        stream: r.stream,
        name: userProfiles.value[id]?.displayName || byId[id]?.name || id || 'User',
        avatar: userProfiles.value[id]?.photoURL || byId[id]?.avatar || ''
      }
    }
    const list = Object.values(byId).sort((a, b) => Number(!!b.stream) - Number(!!a.stream))
    sfuDebug(
      '[SFU] participants computed =>',
      list.map((p) => ({ uid: p.uid, hasStream: !!p.stream }))
    )
    return list
  })

  // Prefer highest layer for primary remote, low layer for others
  watch(
    () => participants.value.map((p) => ({ uid: String(p.uid || ''), hasStream: !!p.stream })),
    async (list) => {
      try {
        const visible = list.filter((p) => p.uid)
        if (!visible.length) return
        const primary = visible[0]
        // Promote primary
        await resumePeerVideo(primary.uid)
        await setPeerVideoLayer(primary.uid, 2)
        // Demote others
        for (const p of visible.slice(1)) {
          await setPeerVideoLayer(p.uid, 0)
        }
      } catch {}
    },
    { immediate: true, deep: true }
  )

  // Build avatar options exactly as AvatarChatDialog did
  async function buildAvatarOptions() {
    avatarsLoading.value = true
    try {
      const uploaded = (await mediaUploadService.listAvatars()).map((record) =>
        mediaUploadService.toOption(record)
      )
      const chars = (characterStore.characters || [])
        .map((char) => {
          const id = String(char?.id || '')
          if (!id) return null
          const src = char.avatar || char.photoURL || ''
          return {
            id: `character:${id}`,
            label: `${char.name || 'Character'}`,
            type: 'image',
            src,
            kind: 'character'
          }
        })
        .filter(Boolean)

      avatarOptions.value = [...chars, ...uploaded]
    } catch {
      avatarOptions.value = []
    } finally {
      avatarsLoading.value = false
    }
  }

  onMounted(async () => {
    try {
      if (!Array.isArray(characterStore.characters) || !characterStore.characters.length) {
        await characterStore.fetchCharacters().catch(() => {})
      }
    } catch {}
    await buildAvatarOptions()
    try {
      if (vcStore?.settings && !vcStore.settings.avatarId && avatarOptions.value.length) {
        const first = avatarOptions.value[0]
        vcStore.settings.avatarId = first.id
        vcStore.settings.avatarSrc = first.src || null
        try {
          await updateSettings({ avatarId: first.id, avatarSrc: first.src || null })
        } catch {}
        try {
          await vcStore.saveSettings?.(vcStore.settings)
        } catch {}
      }
    } catch {}
    try {
      await loadPersistedSettingsForCharacter(settingsCharacterId.value)
    } catch {}
    try {
      await join({ conversationId: props.conversationId, peerId: props.peerId })
    } catch {}
    try {
      await startLocal()
    } catch {}
  })

  onBeforeUnmount(() => {
    if (noAnswerAutoCloseTimer) {
      clearTimeout(noAnswerAutoCloseTimer)
      noAnswerAutoCloseTimer = null
    }
    if (callEndedAutoCloseTimer) {
      clearTimeout(callEndedAutoCloseTimer)
      callEndedAutoCloseTimer = null
    }
    try {
      const id = String(settingsCharacterId.value || '').trim()
      if (id) vcStore?.cancelDebouncedSave?.(id)
    } catch {}
    try {
      leave()
    } catch {}
    stopRinging()
  })

  // Resolve usernames/photos for remote peers and keep a small cache
  watch(
    () =>
      [
        ...(ctrl.current?.targets || []).map((id) => String(id || '')),
        ...(remotePeers.value || []).map((id) => String(id || '')),
        ...(remoteStreams.value || []).map((r) => String(r.peerId || ''))
      ].join(','),
    async () => {
      const idsSet = new Set(
        [
          ...(ctrl.current?.targets || []).map((id) => String(id || '')),
          ...(remotePeers.value || []).map((id) => String(id || '')),
          ...(remoteStreams.value || []).map((r) => String(r.peerId || ''))
        ].filter(Boolean)
      )
      const ids = Array.from(idsSet)
      for (const uid of ids) {
        if (!uid) continue
        if (userProfiles.value[uid]) continue
        try {
          const profile = await userStore.getUserPublicProfile(uid)
          if (profile) {
            userProfiles.value = {
              ...userProfiles.value,
              [uid]: { displayName: profile.displayName, photoURL: profile.photoURL }
            }
          }
        } catch {
          // best-effort; leave fallback to uid
        }
      }
    },
    { immediate: true }
  )

  // Clear unanswered ring timers once a peer appears in this room.
  watch(
    () =>
      (remotePeers.value || [])
        .map((id) => String(id || '').trim())
        .filter(Boolean)
        .join(','),
    () => {
      const peers = (remotePeers.value || []).map((id) => String(id || '').trim()).filter(Boolean)
      peers.forEach((peerId) => {
        try {
          ctrl.markPeerAnswered({ peerId, conversationId: props.conversationId })
        } catch {}
      })
    },
    { immediate: true }
  )

  watch(
    () => normalizePeerIds(remotePeers.value),
    (next, prev) => {
      next.forEach((id) => seenConnectedPeerIds.add(id))
      const nextSet = new Set(next)
      const removed = (Array.isArray(prev) ? prev : []).filter((id) => !nextSet.has(id))
      const previouslyConnected = removed.filter((id) => seenConnectedPeerIds.has(id))
      if (previouslyConnected.length) {
        removeIntendedTargets(previouslyConnected, 'peer-left')
      }
    },
    { immediate: true }
  )

  watch(
    () =>
      normalizePeerIds(
        (remoteStreams.value || []).map((row) => String(row?.peerId || row?.uid || ''))
      ),
    (ids) => {
      ids.forEach((id) => seenConnectedPeerIds.add(id))
    },
    { immediate: true }
  )

  watch(
    () => intendedTargetIds.value.length,
    (count) => {
      if (count > 0) hadOutgoingTargets.value = true
    },
    { immediate: true }
  )

  watch(
    () => [remotePeers.value.length, remoteStreams.value.length],
    ([peerCount, streamCount]) => {
      if (Number(peerCount || 0) > 0 || Number(streamCount || 0) > 0) {
        hadAnyRemoteConnection.value = true
      }
    },
    { immediate: true }
  )

  // Rebuild avatar options when the user's characters list changes
  watch(
    () => characterStore.characters,
    async () => {
      await buildAvatarOptions()
    },
    { deep: true }
  )

  // Update remoteTracksChangedAt whenever remoteStreams changes
  // This signals to ParticipantWindow components to reset their frame detection
  watch(
    () => remoteStreams.value,
    () => {
      remoteTracksChangedAt.value = Date.now()
    },
    { deep: true, flush: 'post' }
  )

  function onMuteToggle() {
    const a = localStream.value?.getAudioTracks?.()[0]
    if (!a) return
    a.enabled = !a.enabled
    micOn.value = a.enabled
  }
  function onToggleVideo() {
    const v = localStream.value?.getVideoTracks?.()[0]
    if (!v) return
    v.enabled = !v.enabled
    camOn.value = v.enabled
  }
  function onHangup() {
    try {
      leave()
    } catch {}
  }
  // Also close the stage so all windows disappear for this user
  watch(
    () => publishing.value,
    (pub) => {
      // no-op, reserved for future auto-close triggers
    }
  )

  function closeStage() {
    try {
      ctrl.closeStage()
    } catch {}
  }

  function notifyRemoteHangup() {
    const conversationId = String(props.conversationId || '').trim()
    if (!conversationId) return
    const selfId = String(props.peerId || '').trim()
    const mode = String(ctrl.current?.mode || '')
      .trim()
      .toLowerCase()

    if (mode === 'campaign' && hadOutgoingTargets.value === true) {
      const recipients = normalizePeerIds([
        ...(ctrl.current?.targets || []),
        ...(remotePeers.value || []),
        ...(remoteStreams.value || []).map((r) => String(r?.peerId || r?.uid || ''))
      ]).filter((id) => id && id !== selfId)
      recipients.forEach((toPeerId) => {
        try {
          ctrl.hangup({ toPeerId, conversationId })
        } catch {}
        // Fallback for servers that don't yet forward `hangup`.
        try {
          ctrl.decline({ toPeerId, conversationId })
        } catch {}
      })
      return
    }

    if (mode === 'direct') {
      const targetId = String(ctrl.current?.targetUid || '').trim()
      if (!targetId || targetId === selfId) return
      try {
        ctrl.hangup({ toPeerId: targetId, conversationId })
      } catch {}
      // Fallback for servers that don't yet forward `hangup`.
      try {
        ctrl.decline({ toPeerId: targetId, conversationId })
      } catch {}
    }
  }

  function onHangupFull() {
    try {
      notifyRemoteHangup()
    } catch {}
    try {
      leave()
    } catch {}
    closeStage()
    stopRinging()
  }

  async function onUpdateSelectedAvatar(id) {
    if (!vcStore?.settings) return
    const opt = avatarOptions.value.find((o) => o.id === id)
    vcStore.settings.avatarId = id || ''
    vcStore.settings.avatarSrc = opt?.src || null
    try {
      await updateSettings({ avatarId: id, avatarSrc: opt?.src || null })
    } catch {}
    try {
      await vcStore.saveSettings?.(vcStore.settings)
    } catch {}
    try {
      const charId = String(settingsCharacterId.value || '').trim()
      if (charId) {
        await loadPersistedSettingsForCharacter(charId)
      }
    } catch {}
    try {
      await updateSettings({
        backgroundId: vcStore.settings.backgroundId || '',
        backgroundSrc: vcStore.settings.backgroundSrc || null
      })
    } catch {}
  }

  async function onUpdateExpressionPreset(id) {
    if (!vcStore?.settings) return
    const preset = resolveExpressionPreset(id || DEFAULT_EXPRESSION_PRESET_ID)
    const payload = {
      expressionPresetId: preset.id,
      expressionStrength: preset.values.expressionStrength,
      faceFitStrength: preset.values.faceFitStrength,
      mouthBoost: preset.values.mouthBoost,
      mouthSmoothing: preset.values.mouthSmoothing,
      browStrength: preset.values.browStrength,
      browSmoothing: preset.values.browSmoothing,
      cheekStrength: preset.values.cheekStrength,
      cheekSmoothing: preset.values.cheekSmoothing
    }
    Object.assign(vcStore.settings, payload)
    try {
      await updateSettings(payload)
    } catch {}
    persistSettingsDebounced()
  }

  async function onUpdateEye(v) {
    if (!vcStore?.settings) return
    vcStore.settings.eye = { ...(v || {}) }
    try {
      await updateSettings({ eye: vcStore.settings.eye })
    } catch {}
    try {
      await vcStore.saveSettings?.(vcStore.settings)
    } catch {}
  }

  async function onUpdateIris(v) {
    if (!vcStore?.settings) return
    vcStore.settings.irisStyle = v
    try {
      await updateSettings({ irisStyle: v })
    } catch {}
    try {
      await vcStore.saveSettings?.(vcStore.settings)
    } catch {}
  }

  async function onUpdateVoice(v) {
    if (!vcStore?.settings) return
    vcStore.settings.voice = { ...(v || {}) }
    try {
      await updateSettings({ voice: vcStore.settings.voice })
    } catch {}
    try {
      await vcStore.saveSettings?.(vcStore.settings)
    } catch {}
  }

  async function onOpenRefineFromWindow() {
    try {
      const existing = String(precomputedHairMaskForCurrentAvatar.value || '').trim()
      if (existing) {
        initialRefineMask.value = existing
      } else {
        const src = selectedAvatar.value?.src || ''
        if (src) {
          try {
            const data = await avatarEngine.buildAvatarInspectorLayers(src, { thumb: false })
            const hair = data?.layers?.hair || ''
            initialRefineMask.value = typeof hair === 'string' ? hair : ''
          } catch {
            initialRefineMask.value = ''
          }
        } else {
          initialRefineMask.value = ''
        }
      }
    } catch {
      initialRefineMask.value = ''
    }
    if (showRefineDialog.value) {
      showRefineDialog.value = false
      nextTick(() => (showRefineDialog.value = true))
    } else {
      showRefineDialog.value = true
    }
  }

  function resolveBuiltinBackgroundSrc(backgroundId) {
    const id = String(backgroundId || '').trim()
    if (!id.startsWith('builtin:')) return null
    const file = id.replace(/^builtin:/, '').trim()
    if (!file) return null
    const base = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '')
    const prefix = String(CHAT_BACKGROUND_PUBLIC_PATH || 'assets/images/chat_background').replace(
      /^\/+/,
      ''
    )
    return `${base}/${prefix}/${file}`
  }

  async function resolveBackgroundSrcForId(backgroundId, fallbackSrc = null) {
    const id = String(backgroundId || '').trim()
    if (!id) return fallbackSrc || null

    const builtInSrc = resolveBuiltinBackgroundSrc(id)
    if (builtInSrc) return builtInSrc

    try {
      const rows = await mediaUploadService.listBackgrounds()
      const row = (rows || []).find((r) => String(r?.id || '').trim() === id)
      if (row) {
        const opt = mediaUploadService.toOption(row)
        if (opt?.src) return opt.src
      }
    } catch {}

    return fallbackSrc || null
  }

  async function loadPersistedSettingsForCharacter(characterId) {
    const id = String(characterId || '').trim()
    if (!id || !vcStore) return
    try {
      const persisted = await vcStore.loadFromCharacter(id)
      if (!persisted || typeof persisted !== 'object') return
      const runtime = vcStore.toRuntime(persisted || {})
      vcStore.enforceDefaultsOn(runtime, { override: false })
      if (runtime.backgroundId) {
        runtime.backgroundSrc = await resolveBackgroundSrcForId(
          runtime.backgroundId,
          runtime.backgroundSrc
        )
      }
      Object.assign(vcStore.settings, runtime)
      sfuDebug('[SFU] loaded persisted settings', {
        characterId: id,
        backgroundId: runtime.backgroundId || '',
        hasBackgroundSrc: !!runtime.backgroundSrc
      })
    } catch (err) {
      console.warn('[SFU] failed to load persisted settings', err?.message || err)
    }
  }

  async function onRefineSaved(payload) {
    const maskSrc = payload?.maskSrc || payload?.cutoutSrc || ''
    const cutoutSrc = payload?.cutoutSrc || payload?.maskSrc || ''
    if (!maskSrc && !cutoutSrc) return
    try {
      await updateSettings({
        precomputedAvatarHairMaskSrc: maskSrc || null,
        precomputedAvatarCutoutSrc: cutoutSrc || null,
        forceAvatarRefresh: true
      })
    } catch {}
    try {
      if (!vcStore?.settings) return
      vcStore.settings.precomputedAvatarHairMaskSrc = maskSrc || null
      vcStore.settings.precomputedAvatarCutoutSrc = cutoutSrc || null
      vcStore.settings.precomputedAvatarCutoutAvatarId = selectedAvatarId.value || ''
      vcStore.settings.precomputedAvatarCutoutAvatarSrc = selectedAvatar.value?.src || ''
      if (!vcStore.settings.avatarCutouts || typeof vcStore.settings.avatarCutouts !== 'object') {
        vcStore.settings.avatarCutouts = {}
      }
      const key = String(selectedAvatarId.value || selectedAvatar.value?.src || '').trim()
      if (key) {
        vcStore.settings.avatarCutouts[key] = {
          id: selectedAvatarId.value || '',
          avatarSrc: selectedAvatar.value?.src || '',
          src: cutoutSrc || '',
          cutoutSrc: cutoutSrc || '',
          maskSrc: maskSrc || '',
          updatedAt: Date.now()
        }
      }
      vcStore.saveSettings?.(vcStore.settings)
    } catch {}
  }

  async function onRefineDeleted() {
    try {
      await updateSettings({
        precomputedAvatarHairMaskSrc: null,
        precomputedAvatarCutoutSrc: null,
        forceAvatarRefresh: true
      })
    } catch {}
    try {
      if (!vcStore?.settings) return
      vcStore.settings.precomputedAvatarHairMaskSrc = null
      vcStore.settings.precomputedAvatarCutoutSrc = null
      vcStore.settings.precomputedAvatarCutoutAvatarId = null
      vcStore.settings.precomputedAvatarCutoutAvatarSrc = null
      const key = String(selectedAvatarId.value || selectedAvatar.value?.src || '').trim()
      if (key && vcStore.settings.avatarCutouts?.[key]) {
        delete vcStore.settings.avatarCutouts[key]
      }
      vcStore.saveSettings?.(vcStore.settings)
    } catch {}
  }

  function persistSettingsDebounced() {
    try {
      const charId = String(settingsCharacterId.value || '').trim()
      if (charId) {
        vcStore?.saveToCharacterDebounced?.(charId, vcStore.settings, { delay: 500 })
      }
    } catch {}
  }

  async function onActionMenu(evt) {
    const key = String(evt?.key || '').toLowerCase()
    if (!key) return
    try {
      if (key === 'refine-selection' || key === 'refine' || key === 'selection') {
        await onOpenRefineFromWindow()
      } else if (key === 'remove-bg') {
        const next =
          typeof evt?.checked === 'boolean' ? !!evt.checked : !vcStore?.settings?.useSegmentation
        await updateSettings({ useSegmentation: next })
        if (vcStore?.settings) {
          vcStore.settings.useSegmentation = next
          persistSettingsDebounced()
        }
      } else {
        const layerKeyByMenuKey = {
          'layer-camera': 'camera',
          'layer-hair': 'avatarHair',
          'layer-face': 'avatarFace',
          'layer-eyes': 'avatarEyes',
          'layer-avatar-image': 'avatarImage',
          'layer-background': 'background'
        }
        const layerKey = layerKeyByMenuKey[key]
        if (!layerKey) return
        const lv = Object.assign({}, vcStore?.settings?.layerVisibility || {})
        const next = typeof evt?.checked === 'boolean' ? !!evt.checked : !lv[layerKey]
        lv[layerKey] = next
        await updateSettings({ layerVisibility: lv })
        if (vcStore?.settings) {
          vcStore.settings.layerVisibility = lv
          persistSettingsDebounced()
        }
      }
    } catch {}
  }

  async function onBgSelect({ id, option }) {
    if (!vcStore?.settings) return
    const src = (await resolveBackgroundSrcForId(id, option?.src ?? null)) ?? null
    vcStore.settings.backgroundId = id || ''
    vcStore.settings.backgroundSrc = src
    try {
      await updateSettings({ backgroundId: vcStore.settings.backgroundId, backgroundSrc: src })
    } catch {}
    persistSettingsDebounced()
  }

  // Ringtone control (mirrors legacy AvatarChatDialog behavior)
  let ringAudio = null
  function startRinging() {
    try {
      if (ringAudio) return
      ringAudio = new Audio('/assets/audio/ring.mp3')
      ringAudio.loop = true
      ringAudio.volume = 0.35
      const p = ringAudio.play()
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          /* autoplay might be blocked; ignore */
        })
      }
    } catch {}
  }
  function stopRinging() {
    try {
      if (ringAudio) {
        ringAudio.pause()
        ringAudio.currentTime = 0
      }
    } catch {}
    ringAudio = null
  }

  // Keep a pre-answer state while peers are present but media is not yet flowing.
  watch(
    () => [remotePeers.value.length, remoteStreams.value.length, hasIntendedTargets.value ? 1 : 0],
    () => {
      const hasPeer = (remotePeers.value || []).length > 0
      const hasMedia = (remoteStreams.value || []).length > 0
      if (hasMedia) {
        if (callStatus.value !== 'active') callStatus.value = 'active'
        return
      }
      if (hasPeer) {
        if (callStatus.value !== 'connecting') callStatus.value = 'connecting'
        return
      }
      if (hasIntendedTargets.value) {
        if (callStatus.value !== 'ringing') callStatus.value = 'ringing'
      } else if (callStatus.value !== 'ended') {
        callStatus.value = 'ended'
      }
    },
    { immediate: true }
  )

  // If call was previously connected and everyone left, fully hang up this stage.
  watch(
    () => [
      callStatus.value,
      hadAnyRemoteConnection.value ? 1 : 0,
      remotePeers.value.length,
      remoteStreams.value.length
    ],
    () => {
      const establishedCallEnded =
        callStatus.value === 'ended' &&
        hadAnyRemoteConnection.value === true &&
        (remotePeers.value || []).length === 0 &&
        (remoteStreams.value || []).length === 0

      if (!establishedCallEnded) {
        if (callEndedAutoCloseTimer) {
          clearTimeout(callEndedAutoCloseTimer)
          callEndedAutoCloseTimer = null
        }
        return
      }

      if (callEndedAutoCloseTimer) return
      callEndedAutoCloseTimer = setTimeout(() => {
        callEndedAutoCloseTimer = null
        onHangupFull()
      }, 600)
    },
    { immediate: true }
  )

  // Ring only while actively dialing intended targets.
  watch(
    () => [callStatus.value, hasIntendedTargets.value ? 1 : 0],
    () => {
      const shouldRing = callStatus.value === 'ringing' && hasIntendedTargets.value
      if (shouldRing) {
        startRinging()
      } else {
        stopRinging()
      }
    },
    { immediate: true }
  )

  // If nobody answered any outgoing targets, auto-close caller stage.
  watch(
    () => [
      callStatus.value,
      hasIntendedTargets.value ? 1 : 0,
      remotePeers.value.length,
      remoteStreams.value.length,
      hadOutgoingTargets.value ? 1 : 0,
      hadAnyRemoteConnection.value ? 1 : 0
    ],
    () => {
      const noAnswerFlow =
        callStatus.value === 'ended' &&
        hadOutgoingTargets.value === true &&
        hadAnyRemoteConnection.value === false &&
        hasIntendedTargets.value === false &&
        (remotePeers.value || []).length === 0 &&
        (remoteStreams.value || []).length === 0

      if (!noAnswerFlow) {
        if (noAnswerAutoCloseTimer) {
          clearTimeout(noAnswerAutoCloseTimer)
          noAnswerAutoCloseTimer = null
        }
        return
      }

      if (noAnswerAutoCloseTimer) return
      noAnswerAutoCloseTimer = setTimeout(() => {
        noAnswerAutoCloseTimer = null
        onHangupFull()
      }, 800)
    },
    { immediate: true }
  )
</script>

<style scoped></style>
